import {
  DATE_FORMAT_NO_TIME,
  DATE_FORMAT_WITH_TIME,
  DateAndTime,
  DateOnly,
  DateTimeString,
  DatetimeValue,
} from "@hex/common";
import moment, { Moment } from "moment-timezone";

export const momentFromDatetimeValue = (
  datetimeValue: DatetimeValue,
): Moment | null => {
  if (datetimeValue && DateAndTime.guard(datetimeValue)) {
    return moment.tz(datetimeValue.isoDateString, datetimeValue.timezoneName);
  } else if (datetimeValue && DateOnly.guard(datetimeValue)) {
    return humanStringToMoment(datetimeValue.dateString);
  } else {
    return null;
  }
};

export const humanStringFromMoment = (
  date: Moment | null,
  includeTime = false,
): string => {
  const dateFormat = includeTime ? DATE_FORMAT_WITH_TIME : DATE_FORMAT_NO_TIME;
  return date ? date.format(dateFormat) : "";
};

export const humanStringFromDate = (
  date: DatetimeValue,
  // if dynamic, check the paylaod to determine if the time should be included.
  includeTime: boolean | "dynamic" = false,
): string => {
  const includeTimeParam =
    includeTime === "dynamic" ? DateAndTime.guard(date) : includeTime;
  return humanStringFromMoment(momentFromDatetimeValue(date), includeTimeParam);
};

export const serializedDatetimeFromDateString = (
  dateString: string,
): string | null => {
  const momentDate = moment(dateString);

  if (momentDate.isValid()) {
    const datetime: DatetimeValue = {
      isoDateString: momentDate.toISOString(),
      timezoneName: moment.tz.guess(),
    };
    return JSON.stringify(datetime);
  } else {
    return null;
  }
};

export const getDay = (d: Date | DateTimeString): string => {
  return moment(d).format("Do");
};

/**
 * Parser user inputed string values to a moment instance.
 * Instead of allowing moment to parse the string without guidance
 * which just delegates to the browser's Date class for parsing,
 * specify known common formats for datetime. There are ambiguous
 * datetime formats such as MM/DD/YYYY <> DD/MM/YYYY. For these,
 * we check how the browser's Date class parses it as it often
 * takes into account the browser locale for determining parse logic
 * which can be helpful in providing additional context.
 */
export const humanStringToMoment = (s: string, requireTime = false): Moment => {
  s = s.trim();

  const unambiguousDateFormats = [
    "YYYY M D",
    "YYYY M Do",
    "YYYY M DD",
    "YYYY MM D",
    "YYYY MM Do",
    "YYYY MM DD",
    "YYYY MMM D",
    "YYYY MMM Do",
    "YYYY MMM DD",
    "YYYY MMMM D",
    "YYYY MMMM Do",
    "YYYY MMMM DD",
    "MMM D YYYY",
    "MMM Do YYYY",
    "MMM DD YYYY",
    "D MMM YYYY",
    "Do MMM YYYY",
    "DD MMM YYYY",
  ];

  const ambiguousDateFormats = [
    "M D YYYY",
    "M DD YYYY",
    "MM D YYYY",
    "MM DD YYYY",
    "D M YYYY",
    "D MM YYYY",
    "DD M YYYY",
    "DD MM YYYY",
  ];

  const timeFormats = [
    "h:m",
    "h:m a",
    "h:m A",
    "h:mm",
    "h:mm a",
    "h:mm A",
    "hh:m",
    "hh:m a",
    "hh:m A",
    "hh:mm",
    "hh:mm a",
    "hh:mm A",
    "H:m",
    "H:m a",
    "H:m A",
    "H:mm",
    "H:mm a",
    "H:mm A",
    "HH:m",
    "HH:m a",
    "HH:m A",
    "HH:mm",
    "HH:mm a",
    "HH:mm A",
  ];

  // strings often formatted as 2010/10/02 or 2010-10-02 so resolve as whitespace
  const r = s.replaceAll(/\/|-/g, " ");

  function tryParseDate(timeFormat?: string): Moment | undefined {
    for (const df of unambiguousDateFormats) {
      const f = timeFormat != null ? `${df} ${timeFormat}` : df;
      const m = moment(r, f, true);
      if (m.isValid()) {
        return m;
      }
    }

    let candidate: Moment | undefined;
    for (const df of ambiguousDateFormats) {
      const f = timeFormat != null ? `${df} ${timeFormat}` : df;
      const m = moment(r, f, true);
      if (m.isValid()) {
        // compare against browser's date parsing as that takes into account locale
        const d = new Date(s);
        if (isNaN(d.getTime())) {
          candidate = m;
        } else if (d.toISOString() === m.toISOString()) {
          return m;
        }
      }
    }

    if (candidate != null) {
      return candidate;
    }
  }

  function tryParseDatetime(): Moment | undefined {
    for (const tf of timeFormats) {
      const parsed = tryParseDate(tf);
      if (parsed != null) {
        return parsed;
      }
    }
  }

  // date and time usually separated by space, e.g. 2020-12-20 10:23
  const likelyHasTime = s.includes(" ");
  const parsed = requireTime
    ? tryParseDatetime()
    : likelyHasTime
      ? (tryParseDatetime() ?? tryParseDate())
      : (tryParseDate() ?? tryParseDatetime());

  if (parsed != null) {
    return parsed;
  }

  const predefinedFormats = [moment.ISO_8601, moment.RFC_2822];
  for (const f of predefinedFormats) {
    const m = moment(s, f, true);
    if (m.isValid()) {
      return m;
    }
  }

  return moment(s);
};
